import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  CloseButton,
  HStack,
  Input,
  ModalProps,
  Stack,
  Text,
  useDisclosure,
  VStack,
  // Select as LegacySelect
} from "@chakra-ui/react";
import AbstractModal from "components/Modal/Abstract";
import configs from "config";
import { SupportedCoinType, useCoin, usePartialState } from "hooks";
import { useCallback, useMemo, useState } from "react";
import { when } from "utils";
import { DropdownIcon, DropdownIconProps } from "./DropdownIcon";
import { Select as LegacySelect } from "./Select";

import toLower from "lodash/toLower";
import EmptyCrate from "components/EmptyCrate/EmptyCrate";

type TokenInfoType = { value: string; label: string };

type TokensType = {
  [key in SupportedCoinType]: TokenInfoType & {
    networks?: TokenInfoType[];
  };
};

type OnSelect = (coin: string, network?: string, country?: string) => void;

export interface CoinSelectProps extends Omit<ButtonProps, "title" | "onSelect"> {
  coin?: string;
  network?: string;
  title?: string;
  showSearch?: string;
  showNetworkOpts?: boolean;
  onSelect?: OnSelect;
  _dropdownIcon?: DropdownIconProps;
  _icon?: BoxProps;
  _label?: BoxProps;

  tokens?: string[];

  isNetworkRequired?: boolean;
  disableSelect?: boolean;
}
export interface CoinSelectModalProps extends Omit<ModalProps, "children" | "title"> {
  coin?: string;
  title?: string;
  network?: string;
  showSearch?: string;
  showNetworkOpts?: boolean;
  onSelect?: OnSelect;
  tokens?: string[];

  isNetworkRequired?: boolean;
}

export interface CoinSelectModalItemProps {
  network?: string;
  info: TokenInfoType & { networks?: TokenInfoType[] };
  onSelect?: OnSelect;
  showNetworkOpts?: boolean;
  onClose: () => void;
  reset: () => void;

  isCurrent?: boolean;
  isNetworkRequired?: boolean;
}

interface InnerState {
  coin: string;
  network: string;
}

export function CoinSelect(props: CoinSelectProps) {
  const {
    network,
    coin,
    title,
    showNetworkOpts = true,
    onSelect,
    tokens,
    showSearch,
    isNetworkRequired,
    _icon,
    _label,
    disableSelect,
    ...xprops
  } = props;

  const { isOpen, onToggle, onClose } = useDisclosure();

  const [state, set] = usePartialState<InnerState>({
    coin: when(!!coin, coin, "btc"),
  });

  const { icon, label } = useCoin((state?.coin! as any) ?? "");

  const handleSelect = (coin: string, network?: string) => {
    set({ coin, network });
    !!onSelect && onSelect(coin, network);
  };

  const handleToggle = () => {
    onToggle();
  };

  return (
    <Box className="bitmama-coin-select" w="fit-content">
      <Button
        minW="unset"
        minH="unset"
        variant="unstyled"
        p="8px"
        w="100%"
        h="100%"
        color="initial"
        fontWeight="500"
        borderRadius="10px"
        border="1px solid transparent"
        textTransform="none"
        _hover={{ bg: "gray.200", borderColor: "primary.600" }}
        _focus={{ bg: "gray.200", borderColor: "primary.600", shadow: "outline" }}
        {...xprops}
        onClick={handleToggle}
        isDisabled={disableSelect}
      >
        <HStack justifyContent="space-between">
          <HStack>
            <Box as={icon} boxSize="24px" {..._icon} />
            <Text fontSize="md" fontWeight="600" {..._label}>
              {label}
            </Text>
          </HStack>

          <DropdownIcon />
        </HStack>
      </Button>

      <Modal
        coin={coin}
        network={network}
        title={title}
        isOpen={isOpen}
        onClose={onClose}
        showNetworkOpts={showNetworkOpts}
        showSearch={showSearch}
        onSelect={handleSelect}
        tokens={tokens}
        isNetworkRequired={isNetworkRequired}
      />
    </Box>
  );
}

export function Modal(props: CoinSelectModalProps) {
  const { coin, network, title, isOpen, onClose, onSelect, tokens: coins, showNetworkOpts, isNetworkRequired } = props;

  const { getTokenInfoFromKnownList } = useCoinTransforms();

  const [search, setSearch] = useState<string | null>();

  const tokens = useMemo(
    () =>
      when(
        !!coins,
        getTokenInfoFromKnownList(coins!),
        getTokenInfoFromKnownList([...configs.supportedPeerCoins, ...configs.supportedPeerCoins])
      ),
    [coins, getTokenInfoFromKnownList]
  );

  const filteredTokens = useMemo(
    () =>
      when(
        !!search,
        tokens.filter((coin) => toLower(`${coin.value} ${coin.label}`).includes(search!)),
        tokens
      ),
    [search, tokens]
  );

  const handleReset = () => {
    setSearch(null);
  };

  return (
    <AbstractModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={false}
      closeOnOverlayClick={false}
      _content={{ maxW: "400px", borderRadius: "10px", p: "20px 16px" }}
    >
      <Stack p="4px" pb="20px">
        <HStack justifyContent="space-between">
          <Text fontSize="xs" fontWeight="600" color="grey.600" textTransform="uppercase">
            {title ?? ""}
          </Text>
          <CloseButton borderRadius="50%" onClick={onClose} />
        </HStack>

        <Input
          minH="42px"
          type="search"
          placeholder="Search Token"
          bg="grey.100"
          borderColor="transparent"
          borderRadius="6px"
          value={search ?? ""}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Stack>

      <VStack px="4px" py="10px" maxH="400px" overflowY="scroll">
        {filteredTokens.map((info, idx) => (
          <Item
            key={idx}
            info={info}
            showNetworkOpts={showNetworkOpts}
            network={network}
            onSelect={onSelect}
            onClose={onClose}
            isCurrent={coin === info.value}
            reset={handleReset}
            isNetworkRequired={isNetworkRequired}
          />
        ))}

        {!!search && filteredTokens.length < 1 && (
          <EmptyCrate
            py="20px"
            type="transaction"
            description={`Tokens with name "${search}" not found`}
            sx={{ svg: { w: "200px", h: "150px", mb: "28px" } }}
          />
        )}
      </VStack>
    </AbstractModal>
  );
}

export function Item(props: CoinSelectModalItemProps) {
  const { isCurrent, isNetworkRequired, network: selectedNetwork, info, onSelect, onClose, reset, showNetworkOpts } = props;

  const [network, setNetwork] = useState<string | undefined>(when(!!isCurrent && !!selectedNetwork, selectedNetwork, undefined));
  const { icon, label } = useCoin(info.value as any);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSelect = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    !!onSelect && onSelect(info.value, network);
    when(!!isNetworkRequired && !network && !!info.networks, () => {}, handleClose)();
  };

  const handleNetwork = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setNetwork(e.target.value);
  };

  return (
    <HStack
      flexDir="row"
      w="100%"
      justifyContent="space-between"
      borderRadius="4px"
      pos="relative"
      _hover={{ bg: "grey.100" }}
      // _focusWithin={{ shadow: "outline" }}
    >
      <Button
        variant="unstyled"
        w="100%"
        minW="unset"
        minH="unset"
        h="fit-content"
        p="10px 20px"
        borderRadius="4px"
        textTransform="none"
        _hover={{ bg: "grey.100" }}
        _focus={{ shadow: "outline" }}
        onClick={handleSelect}
        // onBlur={onClose}
        // onFocus={onClose}
      >
        <HStack>
          <Box as={icon} boxSize="24px" />
          <Box>
            <Text fontSize="sm" fontWeight="600">
              {label}
            </Text>
          </Box>
        </HStack>
      </Button>

      {!!showNetworkOpts && !!info?.networks && info.networks.length > 0 && (
        <LegacySelect
          borderColor="transparent"
          fontSize="sm"
          minW="unset"
          w="fit-content"
          minH="28px"
          h="fit-content"
          borderRadius="4px"
          iconSize="16px"
          bg="transparent"
          placeholder="Network"
          paddingInlineStart="8px"
          paddingInlineEnd="8px"
          value={network ?? ""}
          onChange={handleNetwork}
          pos="absolute"
          right="20px"
          isInvalid={!!isCurrent && !!isNetworkRequired && !network}
          sx={{
            ".chakra-select__icon-wrapper": {
              color: "red",
            },
          }}
        >
          {(info.networks ?? []).map((net, idx) => (
            <option key={idx} value={net.value}>
              {net.label}
            </option>
          ))}
        </LegacySelect>
      )}
    </HStack>
  );
}

function useCoinTransforms() {
  const tokens: Omit<TokensType, "teth" | "tbtc"> = useMemo(
    () => ({
      btc: {
        value: "btc",
        label: "Bitcoin",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      eth: {
        value: "eth",
        label: "Ethereum",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      celo: {
        value: "celo",
        label: "Celo",
        // networks: [{ value: "celo", label: "CELO" }],
      },
      ceur: {
        value: "ceur",
        label: "cEUR",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      cusd: {
        value: "cusd",
        label: "cUSD",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      usdc: {
        value: "usdc",
        label: "USDC",
        networks: [
          { value: "matic", label: "Polygon" },
          { value: "erc20", label: "Ethereum" },
          // { value: "algorand", label: "Algorand" },
          { value: "base", label: "Base Chain" },
          { value: "usdc_celo", label: "Celo" },
          { value: "lisk", label: "LISK" },
        ],
      },
      usdt: {
        value: "usdt",
        label: "USD Tether",
        networks: [
          { value: "tron", label: "Tron" },
          { value: "matic", label: "Polygon" },
          { value: "erc20", label: "Ethereum" },
          { value: "usdt_celo", label: "Celo" },
          { value: "lisk", label: "LISK" },
          // { value: "algorand", label: "Algorand" },
        ],
      },
      pyusd: {
        value: "pyusd",
        label: "PYUSD",
      },
      xlm: {
        value: "xlm",
        label: "Stellar",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      xrp: {
        value: "xrp",
        label: "Ripple",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      matic: {
        value: "matic",
        label: "Polygon",
        // networks: [{ value: "erc20", label: "ERC20" }],
      },
      lisk: {
        value: "lisk",
        label: "LISK",
      },
      ngn: {
        value: "ngn",
        label: "Nigerian Naira",
      },
      ghs: {
        value: "ghs",
        label: "Ghanaian Cedis",
      },
      kes: {
        value: "kes",
        label: "Kenyan Shillings",
      },
      cdf: {
        value: "cdf",
        label: "Congolese Franc",
      },
      xaf: {
        value: "xaf",
        label: "Central African Franc",
      },
      xof: {
        value: "xof",
        label: "West African Franc",
      },
    }),
    []
  );

  const getTokenInfoFromKnownList = useCallback(
    (list: string[]) => {
      const values = Object.values(tokens).filter((tok) => list.includes(tok.value));
      return values;
    },
    [tokens]
  );

  return { tokens, getTokenInfoFromKnownList };
}
